import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CpiDashboardData, formatNumber } from './types';
import './CpiEmissionsMatrix.scss';

interface CpiEmissionsMatrixProps {
  data: CpiDashboardData | null;
  isLoading: boolean;
}

const CpiEmissionsMatrix: FC<CpiEmissionsMatrixProps> = ({ data, isLoading }) => {
  const { t } = useTranslation();

  const formatValue = (value: number | null): string => {
    if (value === null) return '(Blank)';
    if (value === 0) return '-';
    return formatNumber(value, 0);
  };

  /**
   * Euro sign in front of an amount. The `(Blank)` and `-` placeholders stay
   * bare — prefixing them would read as a price rather than as missing data.
   */
  const withEuro = (amount: string): string =>
    amount === '-' || amount === '(Blank)' ? amount : `€ ${amount}`;

  const calculateIndicativeCost = (wtwValueKg: number | null, co2Price: number): string => {
    if (wtwValueKg === null) return '(Blank)';
    if (wtwValueKg === 0) return '-';
    const costInEuros = (wtwValueKg / 1000) * co2Price;
    return withEuro(formatNumber(costInEuros, 0));
  };

  if (isLoading) {
    return (
      <div className="cpi-emissions-matrix cpi-emissions-matrix--loading">
        <div className="cpi-emissions-matrix__skeleton" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Destructure with default values for null safety
  const {
    transportEmissions = { wtwKg: null, wttKg: null, ttwKg: null },
    hubEmissions = { wtwKg: null, wttKg: null, ttwKg: null },
    co2PricePerTon = 0,
  } = data;

  // Calculate totals (use the actual totals from API for the Totaal row)
  const totalWttKg = data.totalEmissionWttKg;
  const totalTtwKg = data.totalEmissionTtwKg;

  return (
    <div className="cpi-emissions-matrix">
      <table className="cpi-emissions-matrix__table">
        <thead>
          <tr>
            <th className="cpi-emissions-matrix__header cpi-emissions-matrix__header--empty" aria-label={t("Row labels")} />
            <th className="cpi-emissions-matrix__header">
              WTW<sub className="cpi-emissions-matrix__unit">kg</sub>
            </th>
            <th className="cpi-emissions-matrix__header">
              WTT<sub className="cpi-emissions-matrix__unit">kg</sub>
            </th>
            <th className="cpi-emissions-matrix__header">
              TTW<sub className="cpi-emissions-matrix__unit">kg</sub>
            </th>
            <th className="cpi-emissions-matrix__header cpi-emissions-matrix__header--cost">
              {t('cpi.indicativeCosts')}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Transport Row */}
          <tr>
            <td className="cpi-emissions-matrix__row-label">
              {t('cpi.transport')}
            </td>
            <td className="cpi-emissions-matrix__cell">
              {formatValue(transportEmissions.wtwKg)}
            </td>
            <td className="cpi-emissions-matrix__cell">
              {formatValue(transportEmissions.wttKg)}
            </td>
            <td className="cpi-emissions-matrix__cell">
              {formatValue(transportEmissions.ttwKg)}
            </td>
            <td className="cpi-emissions-matrix__cell" data-testid="matrix-cost-transport">
              {calculateIndicativeCost(transportEmissions.wtwKg, co2PricePerTon)}
            </td>
          </tr>

          {/* Hub Row */}
          <tr>
            <td className="cpi-emissions-matrix__row-label">
              {t('cpi.hub')}
            </td>
            <td className="cpi-emissions-matrix__cell">
              {formatValue(hubEmissions.wtwKg)}
            </td>
            <td className="cpi-emissions-matrix__cell">
              {hubEmissions.wttKg === null ? '-' : formatValue(hubEmissions.wttKg)}
            </td>
            <td className="cpi-emissions-matrix__cell">
              {hubEmissions.ttwKg === null ? '-' : formatValue(hubEmissions.ttwKg)}
            </td>
            <td className="cpi-emissions-matrix__cell" data-testid="matrix-cost-hub">
              {calculateIndicativeCost(hubEmissions.wtwKg, co2PricePerTon)}
            </td>
          </tr>

          {/* Total Row */}
          <tr>
            <td className="cpi-emissions-matrix__row-label cpi-emissions-matrix__row-label--total">
              {t('cpi.total')}
            </td>
            <td className="cpi-emissions-matrix__cell cpi-emissions-matrix__cell--total">
              {formatValue(data.totalEmissionWtwKg)}
            </td>
            <td className="cpi-emissions-matrix__cell cpi-emissions-matrix__cell--total">
              {formatValue(totalWttKg)}
            </td>
            <td className="cpi-emissions-matrix__cell cpi-emissions-matrix__cell--total">
              {formatValue(totalTtwKg)}
            </td>
            <td
              className="cpi-emissions-matrix__cell cpi-emissions-matrix__cell--total"
              data-testid="matrix-cost-total"
            >
              {withEuro(formatNumber(data.indicativeCostEuros, 0))}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="cpi-emissions-matrix__footer">
        {t('cpi.calculationNote')}
      </div>
    </div>
  );
};

export default CpiEmissionsMatrix;
